import axios from "axios";
import { resolveBackendUrl } from "./apiBase";
import { getStepUpToken, requestStepUp } from "./stepup";

// Cycle 23 hotfix — same-origin on any deployed page (see apiBase.js). The env var
// is only a fallback for non-https local dev.
export const BACKEND_URL = resolveBackendUrl(
  typeof window !== "undefined" ? window.location.origin : "",
  process.env.REACT_APP_BACKEND_URL,
);
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

/** Loyalty 2.0 / M3.4 — one stable anonymous id per browser: the backend's device-trust
 * gate (spend needs ONE email code per device, ever) keys on this header. */
export const deviceId = (() => {
  try {
    let d = localStorage.getItem("inndulge_device_id");
    if (!d) {
      d = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
      localStorage.setItem("inndulge_device_id", d);
    }
    return d;
  } catch { return null; }
})();

/** Cycle 8 — per-device participant token for a check's private bill. */
export const ptKey = (checkNo) => `inndulge_pt_${checkNo}`;

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("inndulge_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  if (deviceId) cfg.headers["x-device-id"] = deviceId;
  const su = getStepUpToken();
  if (su && !cfg.headers["X-Step-Up"]) cfg.headers["X-Step-Up"] = su;
  const m = (cfg.url || "").match(/^(?:\/guest)?\/checks\/(\d+)/);
  if (m && !cfg.headers["X-Participant-Token"]) {
    const pt = localStorage.getItem(ptKey(m[1]));
    if (pt) cfg.headers["X-Participant-Token"] = pt;
  }
  return cfg;
});

// Cycle 7 — sensitive endpoints answer 428 STEP_UP_REQUIRED: open the global
// step-up modal, get a fresh authenticator token, retry the call once.
api.interceptors.response.use(null, async (error) => {
  const cfg = error.config || {};
  const detail = error.response?.data?.detail;
  if (error.response?.status === 428 && detail?.code === "STEP_UP_REQUIRED" && !cfg._stepupRetried) {
    const token = await requestStepUp(); // rejects if the user cancels
    cfg._stepupRetried = true;
    cfg.headers = { ...cfg.headers, "X-Step-Up": token };
    return api.request(cfg);
  }
  // Forced password change (weak password / admin-forced): every staff API answers
  // 403 until a policy-passing password is set — send the user to the sign-in flow,
  // which shows the change-password step on login.
  if (error.response?.status === 403 && detail?.code === "PASSWORD_CHANGE_REQUIRED"
      && typeof window !== "undefined" && window.location.pathname !== "/staff") {
    window.location.assign("/staff");
  }
  return Promise.reject(error);
});

/** Authenticated file download (CSV/JSON exports). */
export const downloadFile = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  saveBlob(res.data, filename);
};

/** Cycle 18 — save a Blob the iPad/iOS-Safari-proof way: the anchor MUST be in the DOM
 * (detached-anchor clicks fail silently on tablets — the client's "nothing downloaded"). */
export const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

/** Cycle 18 — open a print view / link in a NEW tab, never this one.
 * Batch 1 · 1.2 fix: window.open(url, "_blank", "noopener") returns null BY SPEC
 * when the noopener feature is set — so the old `if (!w) location.assign(url)`
 * fallback fired on EVERY call, hijacking the current tab alongside the new one.
 * Now: open plainly (severing opener ourselves); if genuinely popup-blocked, fall
 * back to a synthetic target="_blank" anchor. 🔒 The current tab never navigates. */
export const openTab = (url) => {
  const w = window.open(url, "_blank");
  if (w) {
    try { w.opener = null; } catch { /* cross-origin — already isolated */ }
    return;
  }
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/** Batch 5 — fetch an authenticated PDF (invoice + POD) and open it for print/save. */
export const openPdf = async (path) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  openTab(url);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/** Client-side text download (for JSON blobs / locally-built CSVs). */export const downloadText = (content, filename, mime = "text/plain") => {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/** ZAR display: R1 234.56 (integer cents in, thin-space thousands) */
// SEO/perf (2026-08-20) — tile-sized public media: ?w= is resized+compressed
// server-side (routes_public.serve_media). Full-size only in detail/full views.
export const mediaThumb = (ref, w = 320) =>
  ref ? `${ref}${ref.includes("?") ? "&" : "?"}w=${w}` : ref;


export const fmtR = (cents) => {
  const neg = cents < 0 ? "-" : "";
  const v = (Math.abs(cents) / 100).toFixed(2);
  const [i, d] = v.split(".");
  return `${neg}R${i.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0")}.${d}`;
};

/** Rounded rand, no cents: R512 400 */
export const fmtRk = (cents) =>
  `R${Math.round(cents / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0")}`;

export const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-ZA", {
    timeZone: "Africa/Johannesburg",
    hour: "2-digit",
    minute: "2-digit",
  });

export const fmtDateTime = (iso) =>
  new Date(iso).toLocaleString("en-ZA", {
    timeZone: "Africa/Johannesburg",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

export const minsAgo = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
export const minsUntil = (iso) => Math.round((new Date(iso).getTime() - Date.now()) / 60000);

export const sastDate = (offsetDays = 0) => {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return d.toLocaleDateString("en-CA", { timeZone: "Africa/Johannesburg" }); // YYYY-MM-DD
};

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.message || x.msg || "").filter(Boolean).join(" · ");
  return d.message || JSON.stringify(d);
};

// WS endpoints live under /api/ws/... so the ingress routes them to the backend.
// Derived from BACKEND_URL (same-origin on deployed pages) — https://x → wss://x.
// SEC-001: staff-data channels require a JWT — attach it as ?tok= when signed in.
// Guest channels ignore it (they authorise by capability id / participant token).
export const wsUrl = (path) => {
  const base = BACKEND_URL.replace(/^http/, "ws") + "/api" + path;
  const tok = typeof localStorage !== "undefined" && localStorage.getItem("inndulge_token");
  if (!tok || path.includes("tok=")) return base;  // don't double-append if caller set it
  return base + (path.includes("?") ? "&" : "?") + "tok=" + encodeURIComponent(tok);
};

export const VAT_OF = (incl) => Math.round((incl * 15) / 115);

/** Feb 2026 — per-channel delivery fee preview (server recomputes at order time).
 * Returns null when the channel config is absent/disabled (caller falls back to legacy). */
export const channelFee = (cfg, subtotalCents) => {
  if (!cfg || !cfg.enabled) return null;
  let fee = cfg.mode === "PERCENT"
    ? Math.round((subtotalCents * (cfg.pct || 0)) / 100)
    : (cfg.fixed_cents || 0);
  if (cfg.min_cents) fee = Math.max(fee, cfg.min_cents);
  if (cfg.cap_cents) fee = Math.min(fee, cfg.cap_cents);
  return Math.max(0, fee);
};
